import { useContext, useEffect, useState } from "react";
import { AppName, dataGridConfig, HQISheetName } from "../../constants";
import { DataGrid, Column, Toolbar, Item } from "devextreme-react/data-grid";
import { taxPortalClientApi } from "../../api-client-nswag/taxportal-client-runtime";
import {
  ServiceBusEnum,
  ServiceMessage,
  SiteReport,
} from "../../api-client-nswag/taxportal-client";
import { convertUTCTimeToLocal } from "../../util/Utils";
import { OidcIdentityContext } from "@rsmus/react-auth";
import { snackbarState } from "../../cui/commonComponents/CustomSnackbar";
import { useRecoilState, useSetRecoilState } from "recoil";
import FileSaver from "file-saver";
import { flagUsageMetrics } from "../../App";

type ReportConfigType = {
  displayText: string;
  runText: string;
  reportLocation: string;
};

const reportConfig: Record<string, ReportConfigType> = {
  DOCUMENTS_PER_SITE: {
    displayText: "Count of documents uploaded per active site",
    runText: "Count of documents uploaded per active site",
    reportLocation: "DocumentUploadedPerSite.xlsx"
  },
  DOCUMENTS_COPIED_TO_IMANAGE: {
    displayText: "Count of documents copied to iManage",
    runText: "Count of documents copied to iManage",
    reportLocation: "DocumentCountCopiedToiManage.xlsx"
  },
  WORKSPACE_ASSOCIATED_PER_SITE: {
    displayText: "Workspace associated per site",
    runText: "Workspace associated per site",
    reportLocation: "WorkSpaceAssociatedPerSite.xlsx"
  },
  LEGALENTITES_PER_SITE: {
    displayText: "Legal entities per site",
    runText: "Legal entities per site",
    reportLocation: "LegalEntitiesPerSite.xlsx"
  },
  USER_ASSIGNMENT_REPORT: {
    displayText: "User Assignment and Role Report by Site",
    runText: "User Assignment Report",
    reportLocation: "UserAssignments.xlsx"
  },
  [HQISheetName.INFORMATION_REQUEST]: {
    displayText: "IR Record Count and Last Usage Report by Site",
    runText: "IR Record per site",
    reportLocation: "IRCountPerSite.xlsx"
  },
  [HQISheetName.CONTROVERSY_TRACKER]: {
    displayText: "Controversy Tracker Record Count and Last Usage Report",
    runText: "Controversy Tracker Record per site",
    reportLocation: "ControversyTrackerCountPerSite.xlsx"
  },
  [HQISheetName.K1_TRACKER]: {
    displayText: "K1 Tracker Record Count and Last Usage Report by Site",
    runText: "K1 Tracker per site",
    reportLocation: "K1TrackerCountPerSite.xlsx"
  },
  [HQISheetName.NEW_OPPORTUNITIES_CHECKLIST]: {
    displayText: "Pursuit Checklist Record Count and Last Usage Report by Site",
    runText: "Pursuit Checklist per site",
    reportLocation: "PursuitChecklistCountPerSite.xlsx"
  },
  [HQISheetName.PROJECT_STATUS]: {
    displayText: "Project Status Count and Last Usage Report by Site",
    runText: "Project Status per site",
    reportLocation: "ProjectStatusCountPerSite.xlsx"
  },
  [HQISheetName.CASH_TAX_PAYMENTS]: {
    displayText: "Cash Tax Payments Record Count and Last Usage Report by Site",
    runText: "Cash Tax Payments per site",
    reportLocation: "CashTaxPaymentCountPerSite.xlsx"
  },
  [HQISheetName.NOTICE_TRACKER]: {
    displayText: "Notice Tracker Count and Last Usage Report by Site",
    runText: "Notice Tracker per site",
    reportLocation: "NoticeTrackerCountPerSite.xlsx"
  },
  [HQISheetName.ISSUES_TRACKER]: {
    displayText: "Issues Tracker Count and Last Usage Report by Site",
    runText: "Issues Tracker per site",
    reportLocation: "IssuesTrackerCountPerSite.xlsx"
  },
  [HQISheetName.ESTIMATES_TRACKER]: {
    displayText: "Estimates Tracker Count and Last Usage Report by Site",
    runText: "Estimates Tracker per site",
    reportLocation: "EstimatesTrackerCountPerSite.xlsx"
  },
  [HQISheetName.ENGAGEMENT_OVERVIEW]: {
    displayText: "Engagement Overview Record Count and Last Usage Report by Site",
    runText: "Engagement Overview per site",
    reportLocation: "EngagementOverviewCountPerSite.xlsx"
  },
  [HQISheetName.DELIVERABLE_LISTING]: {
    displayText: "Deliverable listing Record Count and Last Usage Report by Site",
    runText: "Deliverable listing per site",
    reportLocation: "DeliverablelistingCountPerSite.xlsx"
  },
  [HQISheetName.DELIVERABLES]: {
    displayText: "Deliverables Record Count and Last Usage Report by Site",
    runText: "Deliverables per site",
    reportLocation: "DeliverablesCountPerSite.xlsx"
  }
};

const UsageMetricGroup = {
  ISHEET_RECORD_PER_SITE: [
    HQISheetName.INFORMATION_REQUEST,
    HQISheetName.CONTROVERSY_TRACKER,
    HQISheetName.K1_TRACKER,
    HQISheetName.NEW_OPPORTUNITIES_CHECKLIST,
    HQISheetName.PROJECT_STATUS,
    HQISheetName.CASH_TAX_PAYMENTS,
    HQISheetName.NOTICE_TRACKER,
    HQISheetName.ISSUES_TRACKER,
    HQISheetName.ESTIMATES_TRACKER,
    HQISheetName.ENGAGEMENT_OVERVIEW,
    HQISheetName.DELIVERABLE_LISTING,
    HQISheetName.DELIVERABLES,
  ],
};

export interface SiteReportTypes {
  pending: boolean;
  data: SiteReport[];
}

const SiteMetricsRunFunctions = () => {
  const [data, setData] = useState<SiteReportTypes>({
    pending: false,
    data: [],
  });
  const { user } = useContext(OidcIdentityContext);
  const setSnackbar = useSetRecoilState(snackbarState);
  const [usageMetricsFlag, setUsageMetricsFlag] = useRecoilState(flagUsageMetrics);

  const fetchData = async () => {
    try {
      setData({
        pending: true,
        data: [],
      });

      const res = await taxPortalClientApi.usageMetrics_UsageMetrics();
      const uniqueMap = new Map();

      res.forEach(item => {
        const key = item.reportLocation;

        if (key && !uniqueMap.has(key)) {
          uniqueMap.set(key, item);
        }
      });

      const uniqueRes = Array.from(uniqueMap.values());

      const existingLocations = uniqueRes
        .map(item => item.reportLocation)
        .filter(Boolean);

      const updatedReports = Object.keys(reportConfig)
        .filter(reportType =>
          !existingLocations.includes(
            reportConfig[reportType].reportLocation
          )
        )
        .map(reportType => ({
          reportType: reportType,
          reportLocation: reportConfig[reportType].reportLocation
        }));

      setData({
        pending: false,
        data: [...uniqueRes, ...updatedReports],
      });
      setUsageMetricsFlag(false);
    } catch (error) {
      console.error("Error fetching usage metrics:", error);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    if (usageMetricsFlag) {
      fetchData();
    }
  }, [usageMetricsFlag]);

  const handleDownload = async (
    reportLocation: string,
    lastModified: string | null
  ) => {
    try {
      if (lastModified === null || lastModified === undefined) {
        setSnackbar({
          open: true,
          message: "Please run the functionality first",
          severity: "info",
        });
        return;
      }
      await taxPortalClientApi
        .usageMetrics_DownloadUsageMetricFile(reportLocation)
        .then((res: any) => {
          FileSaver.saveAs(res);
        });
    } catch (error) {
      console.error("Error downloading the file:", error);
    }
  };

  const getUsageMetricGroup = (reportType: string): string | undefined => {
    const { normalized } = normalizeReportType(reportType);

    for (const [key, values] of Object.entries(UsageMetricGroup)) {
      if (values.includes(normalized!)) {
        return key;
      }
    }

    return undefined;
  };

  const normalizeReportType = (type?: string) => {
    if (!type) {
      return { normalized: type, appName: undefined };
    }

    const parts = type.split("_");
    const lastPart = parts[parts.length - 1];

    const isAppSuffix = Object.values(AppName).includes(lastPart as any);

    if (isAppSuffix) {
      return {
        normalized: parts.slice(0, -1).join("_"),
        appName: lastPart,
      };
    }

    return {
      normalized: type,
      appName: undefined,
    };
  };


  const cellRenderValue = (cellData: any) => (
    <>
      <span
        onClick={() =>
          handleDownload(
            cellData.data.reportLocation,
            cellData.data.lastModified
          )
        }
        style={{
          color: "#009cde",
          textDecoration: "underline",
          cursor: "pointer",
          margin: "0 5px 0 0",
        }}
      >
        Download
      </span>
      <span
        onClick={() => onRunClick(cellData)}
        style={{
          color: "#009cde",
          textDecoration: "underline",
          cursor: "pointer",
        }}
      >
        Run
      </span>
    </>
  );
  const cellRenderlastExecutionDateTime = (cellData: any) => {
    return cellData.data.lastExecutionDateTime
      ? convertUTCTimeToLocal(cellData.data.lastExecutionDateTime)
      : null;
  };

  const cellRenderDate = (cellData: any) => {
    return cellData.data.lastModified
      ? convertUTCTimeToLocal(cellData.data.lastModified)
      : null;
  };

  const onRunClick = async (cellData: any) => {
    const key = cellData.data.reportSubType ?? cellData.data.reportType;
    const config = reportConfig[key];
    const type = config?.runText || "";

    setSnackbar({
      open: true,
      message: `${type} in progress`,
      severity: "info",
    });

    const groupKey = getUsageMetricGroup(cellData.data.reportType);
    const rawType = cellData.data.reportSubType ?? cellData.data.reportType;
    const { normalized, appName: derivedAppName } = normalizeReportType(rawType);

    const data: ServiceMessage = {
      userName: user?.profile.preferred_username,
      usageMetricsType: groupKey ?? cellData.data.reportType,
      isheetType: normalized,
      eventType: ServiceBusEnum.USAGE_METRICS_REPORT,
    };

    try {
      await taxPortalClientApi.taxPortal_InsertTopic([data]);
    } catch (error) {
      console.error("Error running the report:", error);
    }
  };

  const cellRenderReportType = (cellData: any) => {
    const key = cellData.data.reportSubType ?? cellData.data.reportType;

    const text =
      reportConfig[key]?.displayText ||
      cellData.data.reportSubType ||
      cellData.data.reportType;

    return (
      <div
        title={text}
        style={{
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
        }}
      >
        {text}
      </div>
    );
  };

  const refreshButtonOptions = {
    icon: "refresh",
    onClick: async () => {
      fetchData();
    },
  };

  return (
    <div>
      <DataGrid
        width={"100%"}
        noDataText={data.pending ? "loading..." : "No data"}
        {...dataGridConfig}
        dataSource={data.data}
      >
        <Toolbar>
          <Item
            widget="dxButton"
            options={refreshButtonOptions}
            name="alignRight"
          />
        </Toolbar>
        <Column
          alignment="left"
          cellRender={cellRenderReportType}
          dataField="reportType"
          caption={"Report Name"}
        />
        <Column
          alignment="left"
          cellRender={cellRenderlastExecutionDateTime}
          dataField="lastExecutionDateTime"
          caption={"Scheduler Last Execution Date"}
        />
        <Column
          alignment="left"
          cellRender={cellRenderDate}
          dataField="lastModified"
          caption={"Report Last Run Date"}
        />
        <Column
          alignment="left"
          dataField="action"
          cellRender={cellRenderValue}
          caption={"Action"}
        />
        <Column alignment="left" dataField="reportStatus" caption={"Status"} />
      </DataGrid>
    </div>
  );
};

export default SiteMetricsRunFunctions;
